// import * as logicAnnuncio from "@logic/annuncio";
// import SelectCategory from "@templates/selectCategory";
// import SelectProvince from "@templates/selectProvince";
// import TextInput from '@components/textInput';
import * as logicUser from "@logic/user";
import * as apiPublic from "@api/public";
import * as apiUser from "@api/user";
import AnnuncioPreview from '@templates/annunci/AnnuncioPreview';
import Button from "@components/button";
import Card from "@components/card";
import Icon from "@components/icon";
import React, {useMemo, useState, useCallback, useEffect } from "react";
import moment from 'moment';
import styles from "./AnnuncioGestisci.module.css";
import { ROUTES, useNavigator } from "@contexts/NavigatorContext";
import { getStatoAnnuncio } from "@logic/annuncio";
import { useModals } from "@contexts/ModalsContext";
import { useDialogs } from "@contexts/DialogsContext";
import { ServiceTypeLabels, ValidationStatusLabels, ServiceType } from "@api/apiEnum";
import { useDispatch } from "react-redux";

const AnnuncioGestisci = () => {
  const dispatch = useDispatch();
  const { navigate, currentRoute } = useNavigator();
  const { openModal, closeAllModals } = useModals();
  const { openDialog } = useDialogs();

  const [loading, setLoading] = useState(false);
  const [annuncio, setAnnuncio] = useState(null);

  const idAnnuncio = currentRoute.params[0];

  const statoAnnuncio = useMemo(() => getStatoAnnuncio(annuncio), [annuncio]);

  const formatCents = (cents) => (cents / 100).toFixed(2).replace('.', ',');

  const loadAnnuncio = useCallback(() => {
    setLoading(true);
    apiPublic.GetAdvertisement({ advertisementId: idAnnuncio })
      .then((data) => setAnnuncio(data))
      .finally(() => setLoading(false));
  }, [idAnnuncio])

  const deleteAdvertisement = useCallback(() => {
    setLoading(true);
    apiUser.DeleteAdvertisement({ advertisementId: idAnnuncio })
      .then(() => navigate(ROUTES.I_MIEI_ANNUNCI))
      .finally(() => setLoading(false));
  }, [idAnnuncio, navigate])

  const askDeleteAdvertisement = useCallback(() => {
    openModal({
      title: "Elimina annuncio",
      children: <>
        <div className='row'>
          <div className='col'>
            <span>Confermi l'eliminazione dell'annuncio '{annuncio?.title}'?</span>
          </div>
        </div>
        <div className='row'>
          <div className='col col-flex-center'>
            <Button
              color="primary"
              icon="delete"
              onClick={deleteAdvertisement}
              text="Conferma eliminazione"
            />
            <Button
              color="secondary"
              onClick={closeAllModals}
              text="Annulla"
            />
          </div>
        </div>
      </>,
    });
  }, [annuncio?.title, closeAllModals, deleteAdvertisement, openModal]);

  const refreshUserRealtime = useCallback(async () => {
    await dispatch(logicUser.restoreSignIn());
  }, [dispatch]);

  const buyService = async (serviceType, cost) => {
    setLoading(true);
    try {
      switch(serviceType)
      {
        case 1:
          await apiUser.PurchaseFeatured({ advertisementId: idAnnuncio });
          await refreshUserRealtime();
          loadAnnuncio();
          break;
        case 2:
          await apiUser.ActivateAdvertisement({ advertisementId: idAnnuncio, cost: cost});
          await refreshUserRealtime();
          loadAnnuncio();
          break;
        default:
      }
    } finally {
        setLoading(false);
    }
  };

  const askBuyService = (serviceType, cost) => {
    openDialog({
      title: "Conferma acquisto servizio",
      body: `Vuoi acquistare "${ServiceTypeLabels[serviceType]}" al costo di ${formatCents(cost)} crediti?`,
      confirmButtonText: "Conferma",
      cancelButtonText: "Annulla",
      confirmButtonAction: () => buyService(serviceType, cost),
    });
  };

  const suspend = () => {
    setLoading(true);
    apiUser.SuspendAdvertisement({ advertisementId: idAnnuncio })
      .then((data) => loadAnnuncio())
      .finally(() => setLoading(false));
  };

  const republish = () => {
    setLoading(true);
    apiUser.RepublishAdvertisement({ advertisementId: idAnnuncio })
      .then((data) => loadAnnuncio())
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAnnuncio();
  }, [loadAnnuncio])

  const cardStatoAnnuncio = () => {
    if (!annuncio || !statoAnnuncio) return null;
    const publishOnlineCost = (annuncio.serviceCosts ?? []).find((cost) => cost.serviceType === ServiceType.PublishOnline)?.price ?? 0;
    const featuredCost = (annuncio.serviceCosts ?? []).find((cost) => cost.serviceType === ServiceType.Featured)?.price ?? 0;
    const isMaiStatoAttivato = annuncio.publishDate === "0001-01-01T00:00:00Z";
    const statoIconClassName = [
      styles.icon,
      statoAnnuncio.isSuccess ? styles.success : "",
      statoAnnuncio.error ? styles.error : "",
      statoAnnuncio.warning ? styles.warning : "",
    ].join(" ");
    const iconDefaultProps = {
      fill: 0,
      grade: 0,
      name: statoAnnuncio.icon,
      opticalSize: 24,
      size: 24,
      weight: 400,
    };
    return (
      <Card>
        <div className='row'>
          <div className='col'>
            <div className={styles.statusHeader}>
              <Icon
                {...iconDefaultProps}
                className={statoIconClassName}
              />
              <div className={styles.statusHeaderText}>
                <span className='page-section'>Annuncio {statoAnnuncio.text}</span>
                <span className={styles.statusSubtitle}>Riepilogo e azioni disponibili</span>
              </div>
            </div>
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Validazione</span>
                <span className={styles.infoValue}>{ValidationStatusLabels[annuncio.validationStatus]}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Pubblicazione</span>
                <span className={styles.infoValue}>
                  {isMaiStatoAttivato ? "Mai pubblicato" : moment(annuncio.publishDate).format("D MMMM YYYY")}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{statoAnnuncio.isPagamentoRichiesto ? "Pagamento" : "Scadenza"}</span>
                <span className={styles.infoValue}>
                  {statoAnnuncio.isPagamentoRichiesto
                    ? "Richiesto"
                    : `${annuncio.isExpired ? "Scaduto il " : ""}${moment(annuncio.expirationDate).format("D MMMM YYYY")}`}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Sospeso</span>
                <span className={styles.infoValue}>{annuncio.isSuspended ? "Si" : "No"}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>In evidenza</span>
                <span className={styles.infoValue}>{annuncio.isFeatured ? "Si" : "No"}</span>
              </div>
            </div>
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <span className={styles.actionsTitle}>Azioni annuncio</span>
            <div className={styles.actionsRow}>
              <Button
                color="secondary"
                icon="visibility"
                onClick={() => navigate(ROUTES.ANNUNCIO, [annuncio.id])}
                text="Visualizza"
              />
              {!statoAnnuncio.isRifiutato && (
                <Button
                  color="secondary"
                  icon="edit"
                  onClick={() => navigate(ROUTES.ANNUNCIO_MODIFICA, [annuncio.id])}
                  text="Modifica"
                />
              )}
              <Button
                color="secondary"
                icon="delete"
                onClick={askDeleteAdvertisement}
                text="Elimina"
              />
            </div>
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <span className={styles.actionsTitle}>Gestione stato e visibilita</span>
            <div className={styles.actionsRow}>
              {!annuncio.isSuspended && !annuncio.isRifiutato && (
                <Button
                  color="primary"
                  onClick={() => suspend()}
                  text="Sospendi"
                />
              )}
              {(annuncio.isExpired || annuncio.isSuspended) && !annuncio.isRifiutato && (
                publishOnlineCost === 0 || annuncio.isSuspended ? (
                  <Button
                    color="primary"
                    onClick={() => republish()}
                    text="Ripubblica"
                  />
                ) : (
                  <Button
                    color="primary"
                    onClick={() => askBuyService(ServiceType.PublishOnline, publishOnlineCost)}
                    icon="savings"
                    text={`${ServiceTypeLabels[ServiceType.PublishOnline]} (${formatCents(publishOnlineCost)})`}
                  />
                )
              )}
              {!annuncio.isFeatured && !annuncio.isRifiutato && featuredCost > 0 && (
                <Button
                  color="primary"
                  onClick={() => askBuyService(ServiceType.Featured, featuredCost)}
                  icon="savings"
                  text={`${ServiceTypeLabels[ServiceType.Featured]} (${formatCents(featuredCost)})`}
                />
              )}
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <>
      <br></br>
      <div className='row'>
        <div className='col'>
          <span className='page-title'>Gestisci annuncio</span>
        </div>
      </div>
      {annuncio && (
        <>
          <div className='row'>
            <div className='col'>
              <AnnuncioPreview
                annuncio={annuncio}
                loading={loading}
                suppressNavigation
              />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              {cardStatoAnnuncio()}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AnnuncioGestisci;
